import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Fab,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from "@mui/material";
import DeleteEntityDialog from "components/DeleteEntityDialog";
import RedirectBackButton from "components/RedirectBackButton";
import SnackbarMessage from "components/SnackbarMessage";
import { FORM_ERROR } from "final-form";
import useGlobalContext from "hooks/globalData";
import useBoolean from "hooks/useBoolean";
import { useStrictQuery } from "hooks/useStrictQuery";
import useTitle from "hooks/useTitle";
import useTrashablePromises from "hooks/useTrashablePromises";
import { useState, useCallback, useRef, useEffect } from "react";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQueryClient } from "react-query";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { ResponseOfAdminContentItem } from "services/KnowfullyAdminClient";
import contextStore from "stores/contextStore";
import { Severity, SnackbarMessageProps } from "types/common-types";
import translateError from "utils/translateError";
import DescendantContentBlocksTable from "./DescendantContentBlocksTable";

export const ContentBlockDetails = () => {
  const { formatMessage: t } = useIntl();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const {
    contentBlocksUrl,
    contentBlockEditUrl,
    contentBlockDetailsUrl,
    contentService,
  } = useGlobalContext();
  const [deleteOpen, deleteOpenActions] = useBoolean(false);
  const { registerPromise } = useTrashablePromises();
  const queryClient = useQueryClient();
  const { organizationId, organizationName } = contextStore.useState();

  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const contentBlockNameRef = useRef<string | null>(null);
  const isInitialLoadRef = useRef(true);

  const contentBlockResult = useStrictQuery<ResponseOfAdminContentItem>(
    ["contentService.getContentById", id, organizationId],
    async () => {
      if (!isInitialLoadRef.current && contentBlockNameRef.current) {
        return await contentService.getContentByName(contentBlockNameRef.current);
      }

      const response = await contentService.getContentById(id!);
      if (response?.data?.name && !contentBlockNameRef.current) {
        contentBlockNameRef.current = response.data.name;
      }
      isInitialLoadRef.current = false;
      return response;
    },
    {
      enabled: !!id,
      staleTime: 0,
    }
  );

  const contentBlock = contentBlockResult.data?.data;

  useTitle(contentBlock?.name || "Content Block Details");

  const canDelete = contentBlock?.inheritedOrganizationName === organizationName;

  useEffect(() => {
    if (contentBlock?.id && contentBlock.id !== id) {
      navigate(contentBlockDetailsUrl(contentBlock.id), { replace: true });
    }
  }, [contentBlock?.id, id, navigate, contentBlockDetailsUrl]);

  const handleDelete = useCallback(async () => {
    try {
      await registerPromise(contentService.deleteContent(deleting!));

      queryClient.invalidateQueries(["contentService.getContentBlocks"]);
      queryClient.invalidateQueries(["contentService.getContentById"]);
      queryClient.invalidateQueries(["contentService.getDescendantContentBlocks"]);

      setSnackbarMessage(
        new SnackbarMessageProps(
          t({ id: "contentBlockDeleted" }),
          Severity.Warning
        )
      );
      setDeleting(null);
      deleteOpenActions.setFalse();

      navigate(contentBlocksUrl());
    } catch (err: any) {
      return { [FORM_ERROR]: translateError(err, t) };
    }
  }, [
    contentService,
    contentBlocksUrl,
    deleteOpenActions,
    deleting,
    navigate,
    organizationId,
    queryClient,
    registerPromise,
    t,
  ]);

  if (!id) return <Navigate to={contentBlocksUrl()} replace />;

  const rows = [
    {
      title: t({ id: "organization" }),
      component: <span>{organizationName}</span>,
    },
    {
      title: t({ id: "nameLabel" }),
      component: contentBlock?.name && <span>{contentBlock.name}</span>,
    },
    {
      title: t({ id: "descriptionLabel" }),
      component: contentBlock?.description && <span>{contentBlock.description}</span>,
    },
    {
      title: t({ id: "inheritedFrom" }),
      component: contentBlock?.inheritedOrganizationName && (
        <span>{contentBlock.inheritedOrganizationName}</span>
      ),
    },
    {
      title: t({ id: "valueLabel" }),
      component: contentBlock?.value && (
        <div dangerouslySetInnerHTML={{ __html: contentBlock.value }} />
      ),
    },
  ].filter((r) => !!r.component);

  return (
    <>
      <RedirectBackButton
        redirectRoute={contentBlocksUrl()}
        labelKey="backToListButtonLabel"
      />
      <h2>{contentBlock?.name}</h2>

      <Box my={3} display="flex" justifyContent="center">
        <Box ml={4} />
        <Link to={contentBlockEditUrl(contentBlock?.id || id)} style={{ textDecoration: "none" }}>
          <Fab color="primary" data-cy="editButton">
            <EditIcon />
          </Fab>
        </Link>
        {canDelete && (
          <>
            <Box ml={4} />
            <Fab
              onClick={() => {
                setDeleting(contentBlock?.id || id);
                deleteOpenActions.setTrue();
              }}
            >
              <DeleteIcon />
            </Fab>
          </>
        )}
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableBody>
            {rows.map(({ title, component }, i) => (
              <TableRow key={i}>
                <TableCell
                  component="th"
                  scope="row"
                  style={{ fontWeight: "bold" }}
                >
                  {title}
                </TableCell>
                <TableCell>{component}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {contentBlock && (
        <>
          <Box my={3} />

          <Grid container direction="column" spacing={2}>
            <Grid item>
              <Accordion key="organizationChildren">
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>
                    <T id="childOrganizations" />
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <DescendantContentBlocksTable contentBlock={contentBlock} />
                </AccordionDetails>
              </Accordion>
            </Grid>
          </Grid>
        </>
      )}

      <DeleteEntityDialog
        dialogTitleKey={"deleteContentBlock"}
        contentKey={"confirmDeleteContentBlock"}
        onCancel={deleteOpenActions.setFalse}
        handleDelete={handleDelete}
        open={deleteOpen}
      />

      <SnackbarMessage
        snackbarMessage={snackbarMessage}
        setSnackbarMessage={setSnackbarMessage}
      />
    </>
  );
};
